import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, Crown, User } from "lucide-react";

const LINKS = [
  { id: "mythen", label: "Mythen & Fakten" },
  { id: "probiotika", label: "Probiotika & Natur" },
  { id: "wissenschaft", label: "Wissenschaft" },
  { id: "forschung", label: "Science Deep Dive", route: "/forschung" },
  { id: "zielgruppe", label: "Für wen" },
  { id: "partner", label: "LacLive" },
  { id: "faq", label: "FAQ" },
  { id: "kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 250);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleNav = (l) => {
    setOpen(false);
    if (l.route) {
      navigate(l.route);
      window.scrollTo(0, 0);
    } else {
      goToSection(l.id);
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 border-b ${
        scrolled ? "bg-[#F9F8F3]/95 backdrop-blur-md border-[#E2DDD3] shadow-sm py-2" : "bg-[#F9F8F3]/80 backdrop-blur-sm border-transparent py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <button data-testid="nav-logo" onClick={() => goToSection("top")} className="flex items-center gap-2 shrink-0">
          <img src="/foodbone-logo.png" alt="FoodBone Logo" className="h-11 w-11 object-contain" />
          <span className="font-display text-xl font-bold text-[#1E3F20] tracking-tight">Food<span className="text-[#2D5A27]">&amp;</span>Bone</span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l)}
              className="text-sm font-medium text-[#4A5D52] hover:text-[#1E3F20] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Button
                data-testid="nav-link-premium"
                onClick={() => navigate("/premium")}
                className="bg-[#2D5A27] hover:bg-[#1E3F20] text-white rounded-full px-5"
              >
                <Crown className="w-4 h-4 mr-1.5" /> {user.is_paid ? "Premium" : "Freischalten"}
              </Button>
              <button data-testid="nav-button-logout" onClick={logout} className="text-[#4A5D52] hover:text-[#1E3F20]" title="Abmelden">
                <LogOut className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <Button
                data-testid="nav-button-login"
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-[#1E3F20] hover:bg-[#EBE7DF] rounded-full"
              >
                <User className="w-4 h-4 mr-1.5" /> Anmelden
              </Button>
              <Button
                data-testid="nav-button-paypal-checkout"
                onClick={() => navigate("/premium")}
                className="bg-[#C87D55] hover:bg-[#b56d47] text-white rounded-full px-5"
              >
                <Crown className="w-4 h-4 mr-1.5" /> Premium 19,99 €
              </Button>
            </>
          )}
        </div>

        <button className="lg:hidden text-[#1E3F20]" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F3] border-t border-[#E2DDD3] px-4 py-4 space-y-2">
          {LINKS.map((l) => (
            <button key={l.id} onClick={() => handleNav(l)} className="block w-full text-left py-2 text-[#4A5D52] font-medium">
              {l.label}
            </button>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            {user ? (
              <>
                <Button onClick={() => { setOpen(false); navigate("/premium"); }} className="bg-[#2D5A27] text-white rounded-full">
                  <Crown className="w-4 h-4 mr-1.5" /> {user.is_paid ? "Premium-Bereich" : "Premium freischalten"}
                </Button>
                <Button variant="outline" onClick={() => { setOpen(false); logout(); }} className="rounded-full">Abmelden</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); navigate("/login"); }} className="rounded-full">Anmelden</Button>
                <Button onClick={() => { setOpen(false); navigate("/premium"); }} className="bg-[#C87D55] text-white rounded-full">Premium 19,99 €</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
